import { FC, useState, useEffect, useCallback } from 'react';

import CalculationModal from '../CalculationModal/CalculationModal';
import FacetWrapper from './FacetWrapper';

import { Filter, PopUpRequestObject, FilterBase } from '../../interfaces';
import { hashCode } from '../../utils';
import CalculatorButton from './CalculatorButton/CalculatorButton';
import useTranslation from '../../features/translation/hooks/useTranslation';
import { useFilterChildren } from './hooks/useFilterChildren';
import { extractPopupValue, popupFilterPreparation, preparePopUpRequest } from './utils';
import { FilterValueName } from '../../features/filter/interfaces';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { popupResultValues, selectedFilters, updateFilter, isPopUpLoading } from '../../features/filter/filterSlice';
import { useLocation, useParams } from 'react-router';
import { econdaClickEvent } from '../../Econda';
import { LoadingIndicator } from '@festo-ui/react';
import { ColumnWidth } from '../Parameters/Parameters';
import cn from 'classnames';
import { useWindowDimensions } from 'festo-react-ui-kit/lib/utils/utils';

enum TranslationsKeys {
  MODAL_INPUT = 'calculatorInput',
  MODAL_RESULT = 'calculatorResult',
  CALCULATE = 'calculatorButton',
  OPEN_CALCULATOR = 'calculatorPopUp',
  NO_RESULTS_MESSAGE = 'resultNoResultSecondSentence',
}

interface FiltersProps {
  numberVisibleItems?: number;
  filter: Filter;
  onChange: (filterName: string, value: (string | number)[] | string | number) => void;
}

interface FilterValuesData {
    data?:number,
    max?: number,
}

const SingleFilter: FC<FiltersProps> = (props) => {
  const { filter, onChange } = props;
  const filterValues = filter.value as FilterValuesData;
  const [isOpen, setIsOpen] = useState(false);
  const [popUpRequestData, setPopUpRequestData] = useState<PopUpRequestObject>();
  const [filterWithoutChild, setFilterWithoutChild] = useState<FilterBase>();
  const t = useTranslation();
  const { filtersPopUp, externalUrl, popupType } = useFilterChildren({ filters: filter.filters ?? [] });
  const storedFilters = useAppSelector(selectedFilters);
  const popupResult = useAppSelector(popupResultValues);
  const isLoading = useAppSelector(isPopUpLoading);
  const dispatch = useAppDispatch();
  const url = useParams();
  const { width } = useWindowDimensions();
  const { pathname } = useLocation();

  const obtainRequestPopUp = useCallback((): void => {
    if (popupType) {
      const requestPopup = preparePopUpRequest(popupType, storedFilters);
      if (typeof requestPopup === 'object') {
        setPopUpRequestData(requestPopup);
      }
    }
  }, [popupType, storedFilters]);

  const handleOpen = () => {
    obtainRequestPopUp();
    setIsOpen(true);
    econdaClickEvent(pathname, `${url.sectionName}`, `${filter.label} | open modal`, 'click');
  };
  const handleClose = () => {
    setIsOpen(false);
  };

  const handleOnOk = () => {
    obtainRequestPopUp();
    const maxValue = filterValues.max ?? 0;
    const newValue = Math.min(+popupResult.value.toFixed(2), maxValue);
    dispatch(
      updateFilter({
        name: popupResult.filterName,
        newValue,
      }),
    );
    econdaClickEvent(pathname, `${url.sectionName}`, `${filter.label} | apply filter`, 'click');
    setIsOpen(false);
  };

  let newRow = false;

  const modalBody = (
    <>
      <div className={`${width > 576 ? 'fwe-grid ems-parameters' : ''} modal-body`}>
        {filtersPopUp &&
          filtersPopUp.length > 0 &&
          filtersPopUp.map((filter, index) => {
            const filterIndex = filter.key as FilterValueName;
            const storedValue = storedFilters[filterIndex];
            const colNumber = width > 576 ? ColumnWidth.SIZE_50 : ColumnWidth.SIZE_100;

            if (storedValue === undefined) {
              const valueToExtract = filter.key !== 'condition' ? filter.value : filter.filters![0].value;

              if (valueToExtract !== undefined) {
                const defaultValue = extractPopupValue({ value: valueToExtract });
                dispatch(
                  updateFilter({
                    name: filterIndex,
                    newValue: defaultValue,
                  }),
                );
              }
            }

            const updatedFilter = popupFilterPreparation(filter, t, storedValue as number | string | boolean);
            const content = (
              <div
                key={updatedFilter.id}
                className={cn(`fwe-col-${colNumber} ems-filter-${colNumber}`, {
                  'ems-filter--first': newRow,
                })}
              >
                <SingleFilter
                  filter={updatedFilter}
                  onChange={(filterName, newValue): void => onChange(filterName, newValue)}
                />
              </div>
            );
            newRow = (index + 1) % 2 === 0;

            return content;
          })}
      </div>
      <div className="popup_modal--result__container">
        <h3>{t(TranslationsKeys.MODAL_RESULT)}:</h3>
        {isLoading === 'success' ? (
          <p>{`${popupResult.value.toFixed(2)} ${filterWithoutChild?.unit ?? ''}`}</p>
        ) : isLoading === 'error' ? (
          <p>{t(TranslationsKeys.NO_RESULTS_MESSAGE)}</p>
        ) : (
          <div className="popup_modal--result__container--loading-indicator">
            <LoadingIndicator size="small" />
          </div>
        )}
      </div>
    </>
  );

  useEffect(() => {
    if (popupType) {
      obtainRequestPopUp();
    }
  }, [obtainRequestPopUp, popupType]);

  useEffect(() => {
    if (filtersPopUp.length > 0) {
      const updatedFilter: FilterBase = { ...filter };
      delete updatedFilter['filters'];

      setFilterWithoutChild(updatedFilter);
    }
  }, [filter, filtersPopUp.length]);

  return (
    <>
      <FacetWrapper
        filter={filter}
        key={hashCode(`${filter.id}`.toString())}
        root
        visible
        onChange={(filterName, newValue): void => onChange(filterName, newValue)}
        id={hashCode(`${filter.id}`.toString())}
      />
      {filtersPopUp && popupType && !externalUrl && filtersPopUp.length > 0 ? (
        <CalculatorButton
          popupType={popupType}
          isOpen={isOpen}
          popUpRequest={popUpRequestData ?? undefined}
          onOpen={handleOpen}
          title={`${t(TranslationsKeys.CALCULATE)} ${t(filter.label)}`}
        />
      ) : null}
      {/* Moment of inertia external link */}
      {/* {externalUrl ? (
        <CalculatorButton
          isOpen={isOpen}
          title={`${t(TranslationsKeys.CALCULATE)} ${t(filter.label)}`}
          url={externalUrl}
        />
      ) : null} */}
      <CalculationModal
        isOpen={isOpen}
        onClose={handleClose}
        onCancel={handleClose}
        onOk={handleOnOk}
        title={`${t(TranslationsKeys.OPEN_CALCULATOR)} ${t(filter.label)}`}
        body={modalBody}
      />
    </>
  );
};

export default SingleFilter;
